// src/components/LogoCropper.jsx
import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";

/* ─────────────────────────────────────────
   Allowed formats
───────────────────────────────────────── */
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
const ALLOWED_LABELS = "PNG, JPG, WebP, SVG";

/* ─────────────────────────────────────────
   Remove near-white pixels → transparent
   Tolerance 0-255: higher = removes more
───────────────────────────────────────── */
function removeWhiteBackground(canvas, tolerance = 30) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    // If pixel is close to white → make transparent
    if (r > 255 - tolerance && g > 255 - tolerance && b > 255 - tolerance) {
      data[i + 3] = 0; // alpha = 0
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

/* ─────────────────────────────────────────
   Canvas helper — crops the image,
   removes white background, returns PNG
───────────────────────────────────────── */
const MAX_LOGO_PX = 512; // output size cap

/* ─────────────────────────────────────────
   Reliable image loader — works with data: URLs,
   blob: URLs, remote URLs — no fetch() needed
───────────────────────────────────────── */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error("Image load failed"));
    img.src = src;
  });
}

async function getCroppedImage(imageSrc, croppedAreaPixels, tolerance = 10) {
  const image = await loadImage(imageSrc);

  // Step 1: crop at full resolution into a temp canvas
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = croppedAreaPixels.width;
  tempCanvas.height = croppedAreaPixels.height;
  const tempCtx = tempCanvas.getContext("2d");
  tempCtx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  // Remove white/near-white background pixels
  removeWhiteBackground(tempCanvas, tolerance);

  // Step 2: scale down while preserving the crop's aspect ratio
  const aspect = croppedAreaPixels.width / croppedAreaPixels.height;
  let outW, outH;
  if (aspect >= 1) {
    outW = Math.min(MAX_LOGO_PX, croppedAreaPixels.width);
    outH = Math.round(outW / aspect);
  } else {
    outH = Math.min(MAX_LOGO_PX, croppedAreaPixels.height);
    outW = Math.round(outH * aspect);
  }
  const outCanvas = document.createElement("canvas");
  outCanvas.width = outW;
  outCanvas.height = outH;
  const outCtx = outCanvas.getContext("2d");
  outCtx.drawImage(tempCanvas, 0, 0, outW, outH);

  return outCanvas.toDataURL("image/png");
}

/* Use the entire source image without any crop */
async function getFullImage(imageSrc, tolerance = 10) {
  const image = await loadImage(imageSrc);

  const aspect = image.naturalWidth / image.naturalHeight;
  let outW, outH;
  if (aspect >= 1) {
    outW = Math.min(MAX_LOGO_PX, image.naturalWidth);
    outH = Math.round(outW / aspect);
  } else {
    outH = Math.min(MAX_LOGO_PX, image.naturalHeight);
    outW = Math.round(outH * aspect);
  }

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0, outW, outH);
  removeWhiteBackground(canvas, tolerance);
  return canvas.toDataURL("image/png");
}


/* ─────────────────────────────────────────
   Props:
     onDone(base64)  — called with final cropped image
     onCancel()      — called when modal closed without saving
───────────────────────────────────────── */
export default function LogoCropper({ onDone, onCancel }) {
  const [rawSrc, setRawSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [tolerance, setTolerance] = useState(10); // white removal sensitivity
  const [imageSize, setImageSize] = useState({ w: 1, h: 1 }); // natural image dimensions

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  /* ── File pick ── */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`Unsupported format. Allowed: ${ALLOWED_LABELS}`);
      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be under 2 MB");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const src = reader.result;
      // Read natural dimensions so we can compute correct min zoom
      const img = new Image();
      img.onload = () => setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = src;
      setRawSrc(src);
    };
    reader.readAsDataURL(file);
  };

  /* ── Use full image (no crop) ── */
  const handleUseFullImage = async () => {
    try {
      const full = await getFullImage(rawSrc, tolerance);
      onDone(full);
    } catch (err) {
      toast.error("Failed to process image, please try again");
    }
  };

  /* ── Confirm crop ── */
  const handleCrop = async () => {
    try {
      const cropped = await getCroppedImage(rawSrc, croppedAreaPixels, tolerance);
      onDone(cropped);
    } catch (err) {
      toast.error("Crop failed, please try again");
    }
  };

  // Compute the minimum zoom level that lets the full image be visible in the 280px canvas
  // Canvas width = 420px modal width; canvas height = 280px
  const CANVAS_W = 420;
  const CANVAS_H = 280;
  const minZoom = Math.min(
    CANVAS_W / (imageSize.w || 1),
    CANVAS_H / (imageSize.h || 1),
    1  // never force zoom > 1 as minimum
  );

  return (
    <div className="logo-cropper-overlay">
      <div className="logo-cropper-modal">
        <div className="logo-cropper-header">
          <span>Upload & Crop Logo</span>
          <button className="logo-cropper-close" onClick={onCancel}>✕</button>
        </div>

        {/* ── Step 1: pick file ── */}
        {!rawSrc && (
          <div className="logo-cropper-pick">
            <label className="logo-upload-btn">
              Choose Image
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.svg"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </label>
            <p className="logo-format-hint">Allowed: {ALLOWED_LABELS} · Max 2 MB</p>
          </div>
        )}

        {/* ── Step 2: crop ── */}
        {rawSrc && (
          <>
            <div className="logo-cropper-canvas">
              <Cropper
                image={rawSrc}
                crop={crop}
                zoom={zoom}
                minZoom={Math.max(0.1, minZoom * 0.5)}
                maxZoom={5}
                aspect={undefined}
                restrictPosition={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="logo-cropper-controls">
              <label className="logo-zoom-label">Zoom</label>
              <input
                type="range"
                min={Math.max(0.1, minZoom * 0.5)}
                max={5}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="logo-zoom-slider"
              />
            </div>

            <div className="logo-cropper-controls">
              <label className="logo-zoom-label">
                BG Remove&nbsp;
                <span style={{ color: "#888", fontWeight: 400 }}>({tolerance})</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={tolerance}
                onChange={(e) => setTolerance(Number(e.target.value))}
                className="logo-zoom-slider"
                title="0 = no removal · 100 = remove more white"
              />
            </div>

            <div className="logo-cropper-actions">
              <button
                className="logo-crop-cancel-btn"
                onClick={() => { setRawSrc(null); setCrop({ x: 0, y: 0 }); setZoom(1); }}
              >
                Change Image
              </button>
              <button className="logo-crop-full-btn" onClick={handleUseFullImage}>
                Use Full Image
              </button>
              <button className="logo-crop-save-btn" onClick={handleCrop}>
                Use Cropped
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
